import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import Avatar from "../../../ui/avatar/Avatar";
import MessageContent from "../../mailContent/MessageContent";
import { useModalFocusTrap } from "../../mailContent/useModalFocusTrap";
import ModalOverlay from "../../../ui/Modals/ModalOverlay";
import styles from "./MessageReaderModal.module.css";

const READER_IMAGE_MAX_HEIGHT = 520;

/**
 * Modal para leer un mensaje del hilo con más espacio.
 *
 * Muestra remitente, fecha, destinatarios y asunto, y el mismo cuerpo y
 * adjuntos que el hilo, con imágenes más grandes. Se cierra con Escape, con
 * la cruz o haciendo click afuera.
 */
const MessageReaderModal = ({
  message,
  senderName,
  senderPhoto,
  dateLabel,
  toText,
  ccText,
  onClose,
}) => {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleId = useId();
  const subject = message?.subject || "Sin asunto";

  useModalFocusTrap(dialogRef, closeButtonRef, onClose);

  return createPortal(
    <ModalOverlay className={styles.backdrop} onClose={onClose}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.titleRow}>
            <h2 id={titleId} className={styles.subject}>
              {subject}
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Cerrar mensaje"
            >
              ✕
            </button>
          </div>

          <div className={styles.senderRow}>
            <Avatar src={senderPhoto} name={senderName} size={36} />
            <div className={styles.senderInfo}>
              <span className={styles.senderName}>
                {senderName}
                {message?.from_email && message.from_email !== senderName && (
                  <span className={styles.senderEmail}> &lt;{message.from_email}&gt;</span>
                )}
              </span>
              <span className={styles.date}>{dateLabel}</span>
            </div>
          </div>

          {(toText || ccText) && (
            <dl className={styles.participants}>
              {toText && (
                <>
                  <dt>Para</dt>
                  <dd>{toText}</dd>
                </>
              )}
              {ccText && (
                <>
                  <dt>CC</dt>
                  <dd>{ccText}</dd>
                </>
              )}
            </dl>
          )}
        </header>

        <div className={styles.content}>
          <MessageContent
            message={message}
            bodyClassName={styles.body}
            maxImageHeight={READER_IMAGE_MAX_HEIGHT}
          />
        </div>
      </div>
    </ModalOverlay>,
    document.body,
  );
};

export default MessageReaderModal;
